import { IconButton, styled, Tooltip } from '@mui/material';
import FlexBox from 'Components/FlexBox';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React, { PropsWithChildren } from 'react';
import { Tbooleanish, Booleanish } from 'types/booleanish.types';
import { stopPropagation } from 'utils/event.utils';
import { TBaseTableTypes } from './table.types';

export type TRequiredColumnProps = { width: number | string };

type TTableCellProps<TColumKey> = PropsWithChildren<
  TRequiredColumnProps & {
    columnKey: TColumKey;
    align?: 'left' | 'center' | 'right';
    expanded?: boolean;
    hidden?: boolean;
    hasCollapsable?: boolean;
    onCollapsableClick?: (newCollapseState: boolean) => void;
  }
>;

const CollapsableIconButton = styled(IconButton)<{ collapsed: Tbooleanish }>(({ collapsed }) => ({
  ...(collapsed === 'false' ? { transform: 'rotate(180deg)' } : {}),
  transition: 'transform 2s ease-in-out',
}));

const TableCell = <TColumKey extends string | TBaseTableTypes>({
  children,
  align,
  columnKey,
  expanded,
  width,
  hasCollapsable,
  onCollapsableClick,
}: TTableCellProps<TColumKey>): JSX.Element => {
  const alignItems = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start';

  if (columnKey === 'actions') {
    return (
      <FlexBox
        justifyContent={align ? alignItems : 'flex-end'}
        gap={1}
        width={width}
        maxWidth={width}
        onClick={stopPropagation}
      >
        {children}

        {/* Adding collapsable button dynamically to the end of the action buttons */}
        {hasCollapsable && (
          <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
            <CollapsableIconButton
              collapsed={Booleanish(expanded)}
              onClick={() => onCollapsableClick?.(!expanded)}
            >
              <Icon name="chevron-up" />
            </CollapsableIconButton>
          </Tooltip>
        )}
      </FlexBox>
    );
  }

  return (
    <FlexBox width={width} maxWidth={width} justifyContent={alignItems}>
      {children}
    </FlexBox>
  );
};

export default TableCell;
